//@vendors
const get = require('lodash/object/get');

//@helpers
const { APIGet, APIDel, APIPut, APIPatch, APIPost } = require('utilities/APIRequestHelper');
const apiURLBuilder = require('core/apiURLBuilder');
const {
  PENDING,
  SYNC,
  SECOND_FACTOR_AUTHENTICATION,
  PENDING_SCRAPING_CREDENTIALS,
} = require('constants/aggregator/statuses');
import {
  UPDATING,
  AWAITING_SUPPLEMENTAL_INFORMATION,
  AUTHENTICATING,
} from 'constants/aggregator/tinkStatuses.constants';
const { AGGREGATOR_NEW_FEATURES, AGGREGATOR_PSD2 } = require('constants/flagBasedFeatures');
const notification = require('actions/notifications');
const { getPersonTypeAndCodeHashed } = require('utilities/hash');
import { cipherWithPublicKey } from 'utilities/eurobitsCipher';
const environmentConfig = require('core/environmentConfig');
const APIR_URL = environmentConfig.services.hosts[environmentConfig.baseHost];
const { formatText } = require('core/i18n').i18n;

const { EURO_TEXT } = require('constants/index');
import {
  AGGREGATOR_REMOVE_ENTITY_REQUEST,
  AGGREGATOR_REMOVE_ENTITY_SUCCESS,
  AGGREGATOR_REMOVE_ENTITY_FAILURE,
  AGGREGATOR_TOGGLE_ACTIVE_ENTITY_REQUEST,
  AGGREGATOR_TOGGLE_ACTIVE_ENTITY_SUCCESS,
  AGGREGATOR_TOGGLE_ACTIVE_ENTITY_FAILURE,
  AGGREGATOR_GET_DETAILS_REQUEST,
  AGGREGATOR_GET_DETAILS_SUCCESS,
  AGGREGATOR_GET_DETAILS_FAILURE,
  AGGREGATOR_GET_ENTITIES_DATA_REQUEST,
  AGGREGATOR_GET_ENTITIES_DATA_SUCCESS,
  AGGREGATOR_GET_ENTITIES_DATA_FAILURE,
  AGGREGATOR_SEND_SECOND_KEY_AUTH_REQUEST,
  AGGREGATOR_SEND_SECOND_KEY_AUTH_FAILURE,
  AGGREGATOR_SEND_SECOND_KEY_AUTH_SUCCESS,
  AGGREGATOR_SEND_SECOND_KEY_AUTH_RESET,
  AGGREGATOR_TINK_LINK_SUCCESS,
  AGGREGATOR_TINK_LINK_REQUEST,
  AGGREGATOR_TINK_LINK_FAILURE,
} from 'constants/actionTypes';

const MOVEMENTS_PAGE_SIZE = 10;
const MAX_GLOBAL_POSITION_REQUESTS = 200;

const PRODUCTS_NAME = {
  savings: ['accounts', 'deposits'],
  financing: ['cards', { loansAndMortgages: ['loans', 'mortgages'] }],
  investments: ['funds', 'pensionPlans', 'shares'],
};

const generateProductKey = item => {
  const key = item.stockId ? item.id.concat(item.stockId) : item.id;
  return key;
};

function getScrappingBankStatus(bankStatus, hasScrapingCredentials, isAggregatorPsd2) {
  return !hasScrapingCredentials && isAggregatorPsd2 ? PENDING_SCRAPING_CREDENTIALS : bankStatus;
}

const parseTinkEntitiesData = (data, isAggregatorPsd2, isOnRequestPool = false) => {
  const emptyBalance = { amount: 0, currency: EURO_TEXT };
  let banks = {};
  let products = {};
  let aggregation = {
    account: {
      type: 'account',
      balance: (data && data.savings.accounts.total.visible.balances[0]) || emptyBalance,
      total: data && data.savings.accounts.total.visible.total,
    },
    card: {
      type: 'card',
      balance: (data && data.financing.cards.total.visible.balances[0]) || emptyBalance,
      total: data && data.financing.cards.total.visible.total,
    },
    deposit: {
      type: 'deposit',
      balance: (data && data.savings.deposits.total.visible.balances[0]) || emptyBalance,
      total: data && data.savings.deposits.total.visible.total,
    },
    fund: {
      type: 'fund',
      balance: (data && data.investments.funds.total.visible.balances[0]) || emptyBalance,
      total: data && data.investments.funds.total.visible.total,
    },
    investment: {
      type: 'investment',
      balance: (data && data.investments.total.visible.balances[0]) || emptyBalance,
      total: data && data.investments.total.visible.total,
    },
    loanandmortgage: {
      type: 'loanandmortgage',
      balance: (data && data.financing.loansAndMortgages.total.visible.balances[0]) || emptyBalance,
      total: data && data.financing.loansAndMortgages.total.visible.total,
    },
    pensionplan: {
      type: 'pensionplan',
      balance: (data && data.investments.pensionPlans.total.visible.balances[0]) || emptyBalance,
      total: data && data.investments.pensionPlans.total.visible.total,
    },
    share: {
      type: 'share',
      balance: (data && data.investments.shares.total.visible.balances[0]) || emptyBalance,
      total: data && data.investments.shares.total.visible.total,
    },
  };

  const savingsTotal =
    data && data.savings.total.visible.balances[0]
      ? data && data.savings.total.visible.balances[0].amount
      : emptyBalance.amount;
  const investmentsTotal =
    data && data.investments.total.visible.balances[0]
      ? data && data.investments.total.visible.balances[0].amount
      : emptyBalance.amount;

  const savingAndInvestment = {
    amount: savingsTotal + investmentsTotal,
    currency: EURO_TEXT,
  };

  const financingTotal = (data && data.financing.total.visible.balances[0]) || emptyBalance;

  const total = data && data.total.visible.balances[0];

  data &&
    get(data, 'banks', []).forEach(bank => {
      const getInvestments = () => {
        const funds = bank.investments.funds.items.map(item => item.id);
        const pensionPlans = bank.investments.pensionPlans.items.map(item => item.id);
        const shares = bank.investments.shares.items.map(item => item.id);

        return [...funds, ...pensionPlans, ...shares];
      };

      const getLoansAndMortgages = () => {
        const loans = bank.financing.loansAndMortgages.loans.items.map(item => item.id);
        const mortgages = bank.financing.loansAndMortgages.mortgages.items.map(item => item.id);

        return [...loans, ...mortgages];
      };

      const investmentsItems = getInvestments();
      const loansAndMortgagesItems = getLoansAndMortgages();

      banks[bank.userBankId] = {
        cacheable: bank.cacheable,
        code: bank.code,
        imageUrl: bank.imageUrl,
        name: bank.name,
        userBankId: bank.userBankId,
        robotName: bank.robotName,
        // TINK
        tinkStatus: bank.tinkStatus, // tinkScrappingStatus
        tinkPsd2Status: bank.tinkPsd2Status, // tinkPsd2Status
        tinkPsd2CredentialsId: bank.tinkPsd2CredentialsId, // tinkPsd2Credentials
        tinkCredentialId: bank.tinkCredentialId, // tinkScrappingCredentials
        // EUROBITS
        status: bank.status, // eurobitsStatusScrapping
        psd2Status: bank.psd2Status, // eurobitsPsd2Status
        psd2Credentials: bank.psd2Credentials, // eurobitsPsd2Credentials
        scrapingCredentials: bank.scrapingCredentials, // eurobitsScrappingCredentials
        scrappingStatus: getScrappingBankStatus(
          bank.tinkStatus || bank.status,
          bank.scrapingCredentials,
          isAggregatorPsd2
        ),
        secondFactorAuth:
          bank.tinkPsd2Status === AWAITING_SUPPLEMENTAL_INFORMATION ||
          bank.tinkStatus === AWAITING_SUPPLEMENTAL_INFORMATION ||
          bank.status === SECOND_FACTOR_AUTHENTICATION ||
          bank.psd2Status === SECOND_FACTOR_AUTHENTICATION,
        account: bank.savings.accounts,
        card: bank.financing.cards,
        loanandmortgage: bank.financing.loansAndMortgages,
        deposit: bank.savings.deposits,
        investment: bank.investments,
        products: {
          account: {
            balance: bank.savings.accounts.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            items: bank.savings.accounts.items.map(item => item.id),
            name: 'account',
            itemsNumber: bank.savings.accounts.items.length,
          },
          card: {
            balance: bank.financing.cards.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            items: bank.financing.cards.items.map(item => item.id),
            name: 'card',
            itemsNumber: bank.financing.cards.items.length,
          },
          deposit: {
            balance: bank.savings.deposits.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            items: bank.savings.deposits.items.map(item => item.id),
            name: 'deposit',
            itemsNumber: bank.savings.deposits.items.length,
          },
          investment: {
            balance: bank.investments.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            name: 'investment',
            items: investmentsItems,
            itemsNumber: investmentsItems.length,
          },
          loanandmortgage: {
            balance: bank.financing.loansAndMortgages.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            name: 'loanandmortgage',
            items: loansAndMortgagesItems,
            itemsNumber: loansAndMortgagesItems.length,
          },
        },
        subproducts: {
          investment: {
            balance: bank.investments.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            name: 'investment',
            items: investmentsItems,
            itemsNumber: investmentsItems.length,
          },
          loanandmortgage: {
            balance: bank.financing.loansAndMortgages.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            name: 'loanandmortgage',
            items: loansAndMortgagesItems,
            itemsNumber: loansAndMortgagesItems.length,
          },
          fund: {
            name: 'fund',
            items: bank.investments.funds.items,
            balance: bank.investments.funds.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            itemsNumber: bank.investments.funds.items.length,
          },
          pensionplan: {
            name: 'pensionplan',
            items: bank.investments.pensionPlans.items,
            balance: bank.investments.pensionPlans.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            itemsNumber: bank.investments.pensionPlans.items.length,
          },
          share: {
            name: 'share',
            items: bank.investments.shares.items,
            balance: bank.investments.shares.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            itemsNumber: bank.investments.shares.items.length,
          },
          loan: {
            name: 'loan',
            items: bank.financing.loansAndMortgages.loans.items,
            balance: bank.financing.loansAndMortgages.loans.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            itemsNumber: bank.financing.loansAndMortgages.loans.items.length,
          },
          mortgage: {
            name: 'mortgage',
            items: bank.financing.loansAndMortgages.mortgages.items,
            balance: bank.financing.loansAndMortgages.mortgages.total.visible.balances[0] || {
              amount: 0,
              currency: 'EUR',
            },
            itemsNumber: bank.financing.loansAndMortgages.mortgages.items.length,
          },
        },
      };

      Object.keys(PRODUCTS_NAME)
        .map(type =>
          PRODUCTS_NAME[type].map(subtype => {
            if (typeof subtype === 'object') {
              return Object.keys(subtype).map(prodSubtype => {
                return subtype[prodSubtype].map(product => bank[type][prodSubtype][product].items);
              });
            } else {
              return bank[type][subtype].items;
            }
          })
        )
        .flat(4)
        .map(product => {
          product.bank = product.userBankId;
          product.product = product.subproductType;
          product.subproduct = product.subproductType;
          return product;
        })
        .map(product => (products[product.id] = product));
    });

  return {
    aggregation,
    banks,
    products,
    financing: financingTotal,
    savingAndInvestment,
    total,
    isOnRequestPool,
  };
};

const parseEntitiesData = (data, isAggregatorPsd2) => {
  let banks = {};
  let products = {};
  let aggregation = data.products
    .map(p => ({ [p.type.toLowerCase()]: { ...p, type: p.type.toLowerCase() } }))
    .reduce((lv, cv) => ({ ...lv, ...cv }), {});

  get(data, 'banks', []).forEach(bank => {
    banks[bank.userBankId] = {
      robotName: bank.robotName,
      imageUrl: bank.imageUrl,
      status: getScrappingBankStatus(bank.status, bank.scrapingCredentials, isAggregatorPsd2),
      products: {},
      subproducts: {},
      name: bank.name,
      userBankId: bank.userBankId,
      code: bank.code,
      secondFactorAuth: bank.status === SECOND_FACTOR_AUTHENTICATION,
      scrapingCredentials: bank.scrapingCredentials,
      psd2Credentials: bank.psd2Credentials,
      psd2Status: bank.psd2Status,
    };

    get(bank, 'products', []).forEach(p => {
      banks[bank.userBankId]['products'][p.type.toLowerCase()] = {
        balance: p.balance,
        name: p.type.toLowerCase(),
        itemsNumber: p.total,
        items: [],
      };
      get(p, 'subproducts', []).forEach(sp => {
        banks[bank.userBankId]['subproducts'][sp.type.toLowerCase()] = {
          balance: sp.balance,
          name: sp.type.toLowerCase(),
          items: [],
          stockBalance: sp.stockBalance,
        };
        get(sp, 'items', []).forEach(i => {
          const keyProduct = generateProductKey(i);
          products[keyProduct] = {
            ...i,
            product: p.type.toLowerCase(),
            subproduct: sp.type.toLowerCase(),
            bank: bank.userBankId,
          };
          banks[bank.userBankId]['products'][p.type.toLowerCase()]['items'].push(keyProduct);
          banks[bank.userBankId]['subproducts'][sp.type.toLowerCase()]['items'].push(keyProduct);
        });
      });
    });
  });

  return {
    banks,
    products,
    aggregation,
    monthExpenses: data['monthExpenses'],
    monthIncome: data['monthIncome'],
    financing: data['financing'] ? data['financing'] : { amount: 0, currency: EURO_TEXT },
    savingAndInvestment: data['savingAndInvestment']
      ? data['savingAndInvestment']
      : { amount: 0, currency: EURO_TEXT },
  };
};

function getConsentUpdateTinkLinkSuccess(data) {
  return {
    type: AGGREGATOR_TINK_LINK_SUCCESS,
    payload: {
      data,
    },
  };
}

function getConsentUpdateTinkLinkRequest() {
  return {
    type: AGGREGATOR_TINK_LINK_REQUEST,
  };
}

function getConsentUpdateTinkLinkFailure(error) {
  return {
    type: AGGREGATOR_TINK_LINK_FAILURE,
    payload: {
      error,
    },
  };
}

function removeEntityRequest() {
  return {
    type: AGGREGATOR_REMOVE_ENTITY_REQUEST,
  };
}

function removeEntitySuccess(entityId) {
  return {
    type: AGGREGATOR_REMOVE_ENTITY_SUCCESS,
    payload: {
      entityId,
    },
  };
}

function removeEntityFailure(error) {
  return {
    type: AGGREGATOR_REMOVE_ENTITY_FAILURE,
    payload: {
      error,
    },
  };
}

function toggleActiveEntityRequest() {
  return {
    type: AGGREGATOR_TOGGLE_ACTIVE_ENTITY_REQUEST,
  };
}

function toggleActiveEntitySuccess(entityId, value) {
  return {
    type: AGGREGATOR_TOGGLE_ACTIVE_ENTITY_SUCCESS,
    payload: {
      entityId,
      value,
    },
  };
}

function toggleActiveEntityFailure(error, entityId, value) {
  return {
    type: AGGREGATOR_TOGGLE_ACTIVE_ENTITY_FAILURE,
    payload: {
      error,
      entityId,
      value,
    },
  };
}

function getDetailsByProductRequest(productId, newRequest) {
  return {
    type: AGGREGATOR_GET_DETAILS_REQUEST,
    payload: {
      productId,
      newRequest,
    },
  };
}

function getDetailsByProductSuccess(productId, details, isNew = true) {
  return {
    type: AGGREGATOR_GET_DETAILS_SUCCESS,
    payload: {
      productId,
      details,
      isNew,
    },
  };
}

function getDetailsByProductFailure(productId, error) {
  return {
    type: AGGREGATOR_GET_DETAILS_FAILURE,
    payload: {
      error,
      productId,
    },
  };
}

function getEntitiesDataRequest(firstRequest) {
  return {
    type: AGGREGATOR_GET_ENTITIES_DATA_REQUEST,
    payload: {
      firstRequest,
    },
  };
}

function getEntitiesDataSuccess(data) {
  return {
    type: AGGREGATOR_GET_ENTITIES_DATA_SUCCESS,
    payload: {
      data,
    },
  };
}

function getEntitiesDataFailure(error) {
  return {
    type: AGGREGATOR_GET_ENTITIES_DATA_FAILURE,
    payload: {
      error,
    },
  };
}

function sendSecondKeyAuthRequest() {
  return {
    type: AGGREGATOR_SEND_SECOND_KEY_AUTH_REQUEST,
  };
}

function sendSecondKeyAuthFailure() {
  return {
    type: AGGREGATOR_SEND_SECOND_KEY_AUTH_FAILURE,
  };
}

function sendSecondKeyAuthSuccess() {
  return {
    type: AGGREGATOR_SEND_SECOND_KEY_AUTH_SUCCESS,
  };
}

function resetSecondKeyFactorAuth() {
  return {
    type: AGGREGATOR_SEND_SECOND_KEY_AUTH_RESET,
  };
}

const verifyHavePendingOrSyncStatusTink = bank =>
  bank.tinkPsd2Status === UPDATING ||
  bank.tinkPsd2Status === AUTHENTICATING ||
  bank.tinkStatus === UPDATING ||
  bank.tinkStatus === AUTHENTICATING;

const verifyIfShouldStartRequestPoolTink = bankList => {
  if (!bankList || bankList.length === 0) return false;

  return bankList.some(verifyHavePendingOrSyncStatusTink);
};

const verifyHavePendingOrSyncStatus = bank =>
  bank.status === PENDING ||
  bank.psd2Status === PENDING ||
  bank.status === SYNC ||
  bank.psd2Status === SYNC;

const verifyIfShouldStartRequestPool = bankList => {
  if (!bankList || bankList.length === 0) return false;

  return bankList.some(verifyHavePendingOrSyncStatus);
};

const getEntitiesData =
  (first = null, tries = 0, force = false) =>
  (dispatch, getState) => {
    if (__CONTACT_CENTER__) return;

    const firstCall = first === null ? getState().aggregatorData.get('firstRequest') : first;

    const AGGREGATOR_V2 = getState().flagBasedFeatures
      ? getState().flagBasedFeatures.get(AGGREGATOR_NEW_FEATURES)
      : false;
    const IS_AGGREGATOR_PSD2 = getState().flagBasedFeatures
      ? getState().flagBasedFeatures.get(AGGREGATOR_PSD2)
      : false;

    const isFetching = getState().aggregatorData.get('isFetching');
    const error = getState().aggregatorData.get('error');

    let timeoutObject = null;
    const queryParams = {
      query: {
        first: firstCall,
      },
    };

    if ((!isFetching && !error) || force) {
      dispatch(getEntitiesDataRequest(false));
      return APIGet({
        dispatch,
        endpoint: AGGREGATOR_V2 ? 'aggregatorGlobalPosition' : 'aggregatorGlobalPositionV1',
        url: AGGREGATOR_V2
          ? apiURLBuilder.getURL('aggregatorGlobalPosition', {
              userId: getPersonTypeAndCodeHashed(),
            })
          : apiURLBuilder.getURL('aggregatorGlobalPositionV1'),
        params: queryParams,
      })
        .then(data => {
          data = data ? data : { banks: [], products: [] };
          const banks = data.banks;
          const mustStartRequestPool = verifyIfShouldStartRequestPool(banks);

          if (tries <= MAX_GLOBAL_POSITION_REQUESTS) {
            if (mustStartRequestPool)
              timeoutObject = setTimeout(
                () => !__CONTACT_CENTER__ && dispatch(getEntitiesData(false, tries + 1)),
                20000
              );
            try {
              dispatch(getEntitiesDataSuccess(parseEntitiesData(data, IS_AGGREGATOR_PSD2)));
            } catch (error) {
              clearTimeout(timeoutObject);
              dispatch(getEntitiesDataFailure({ error: error }));
            }
          } else if (banks.every(verifyHavePendingOrSyncStatus)) {
            clearTimeout(timeoutObject);
            dispatch(getEntitiesDataFailure({ error: 'too much tries' }));
          } else {
            clearTimeout(timeoutObject);
            dispatch(getEntitiesDataFailure({ error: null }));
          }
        })
        .catch(error => {
          clearTimeout(timeoutObject);
          dispatch(getEntitiesDataFailure({ error: error }));
        });
    }
  };

const requestNextPage =
  (productId, url = null) =>
  (dispatch, getState) => {
    dispatch(getDetailsByProductRequest(productId, !url));

    const AGGREGATOR_V2 = getState().flagBasedFeatures
      ? getState().flagBasedFeatures.get(AGGREGATOR_NEW_FEATURES)
      : false;

    return APIGet({
      dispatch,
      url: AGGREGATOR_V2 ? `${APIR_URL}/agre/${url}` : url,
      endpoint: AGGREGATOR_V2 ? 'aggregatorGetMovements' : 'aggregatorGetMovementsV1',
    })
      .then(response => {
        const { movements, last, _links } = response;
        const nextPage = _links && _links.nextPage;
        const href = nextPage && nextPage.href;

        dispatch(
          getDetailsByProductSuccess(productId, { movements, hasMore: !last, url: href }, !url)
        );
      })
      .catch(error => dispatch(getDetailsByProductFailure(productId, error)));
  };

const getProductDetails =
  (userBankId, productType, productId, url = null, filters = {}, size = MOVEMENTS_PAGE_SIZE) =>
  (dispatch, getState) => {
    dispatch(getDetailsByProductRequest(productId, !url));
    const requestData = !url
      ? {
          query: {
            page: 0,
            size,
            ...filters,
          },
        }
      : undefined;

    const AGGREGATOR_V2 = getState().flagBasedFeatures
      ? getState().flagBasedFeatures.get(AGGREGATOR_NEW_FEATURES)
      : false;

    //whith state banks, and ID

    return (
      APIGet(
        dispatch,
        url ||
          apiURLBuilder.getURL(
            AGGREGATOR_V2 ? 'aggregatorGetMovements' : 'aggregatorGetMovementsV1',
            {
              userId: getPersonTypeAndCodeHashed(),
              userBankId,
              productType: productType,
              productId,
            }
          ),
        requestData
      )
        // return getDetailsMock()
        .then(response => {
          const { movements, last, _links } = response;
          const nextPage = _links && _links.nextPage;
          const href = nextPage && nextPage.href;

          dispatch(
            getDetailsByProductSuccess(productId, { movements, hasMore: !last, url: href }, !url)
          );
        })
        .catch(error => dispatch(getDetailsByProductFailure(productId, error)))
    );
  };

const removeEntity = userBankId => (dispatch, getState) => {
  dispatch(removeEntityRequest());

  const AGGREGATOR_V2 = getState().flagBasedFeatures
    ? getState().flagBasedFeatures.get(AGGREGATOR_NEW_FEATURES)
    : false;

  if (AGGREGATOR_V2) {
    return APIDel({
      dispatch,
      endpoint: 'aggregatorGlobalPosition',
      url: apiURLBuilder.getURL('aggregatorUserBankOperations', {
        userId: getPersonTypeAndCodeHashed(),
        userBankId,
      }),
    })
      .then(() => {
        dispatch(removeEntitySuccess(userBankId));
        // if (banks.size == 1)
      })
      .then(() => {
        dispatch(
          notification.success({ message: formatText('aggregatorMessages-removeEntitySuccess') })
        );
      })
      .catch(error => {
        dispatch(removeEntityFailure({ error: error }));
        dispatch(
          notification.error({ message: formatText('aggregatorMessages-removeEntityError') })
        );
      });
  } else {
    const requestData = {
      query: {
        userBankId,
      },
    };

    return APIPost(dispatch, apiURLBuilder.getURL('aggregatorDeleteBankV1'), requestData)
      .then(() => {
        dispatch(removeEntitySuccess(userBankId));
        // if (banks.size == 1)
      })
      .then(() => {
        dispatch(
          notification.success({ message: formatText('aggregatorMessages-removeEntitySuccess') })
        );
      })
      .catch(error => {
        dispatch(removeEntityFailure({ error: error }));
        dispatch(
          notification.error({ message: formatText('aggregatorMessages-removeEntityError') })
        );
      });
  }
};

const getSecondFactorAuth = userBankId => dispatch => {
  return APIGet({
    dispatch,
    endpoint: 'aggregatorUserBankOperations',
    url: apiURLBuilder.getURL('aggregatorUserBankOperations', {
      userId: getPersonTypeAndCodeHashed(),
      userBankId,
    }),
  })
    .then(() => {
      dispatch(
        notification.success({
          message: formatText('aggregatorMessages-getSecondFactorKeySucccess'),
        })
      );
    })
    .catch(() =>
      dispatch(
        notification.error({ message: formatText('aggregatorMessages-getSecondFactorKeyFail') })
      )
    );
};

const sendSecondAuthKey = (pwdVble, userBankId, bankCode) => dispatch => {
  dispatch(sendSecondKeyAuthRequest());

  return APIGet(dispatch, apiURLBuilder.getURL('aggregatorGetDataBank', { robotCode: bankCode }))
    .then(response => {
      const requestData = {
        query: {
          pwdVble: cipherWithPublicKey(pwdVble, response.publicKey),
        },
      };

      APIPut({
        dispatch,
        endpoint: 'aggregatorUserBankOperations',
        url: apiURLBuilder.getURL('aggregatorUserBankOperations', {
          userId: getPersonTypeAndCodeHashed(),
          userBankId,
        }),
        params: requestData,
      })
        .then(() => dispatch(sendSecondKeyAuthSuccess()))
        .catch(() => dispatch(sendSecondKeyAuthFailure()));
    })
    .catch(() => dispatch(sendSecondKeyAuthFailure()));
};

const toggleActive = (productId, productType, value, userBankId) => (dispatch, getState) => {
  dispatch(toggleActiveEntityRequest());
  dispatch(toggleActiveEntitySuccess(productId, value));

  const AGGREGATOR_V2 = getState().flagBasedFeatures
    ? getState().flagBasedFeatures.get(AGGREGATOR_NEW_FEATURES)
    : false;

  const requestData = {
    query: {
      visible: value,
    },
  };

  if (AGGREGATOR_V2) {
    return APIPatch({
      dispatch,
      endpoint: 'aggregatorToggle',
      url: apiURLBuilder.getURL('aggregatorToggle', {
        userId: getPersonTypeAndCodeHashed(),
        userBankId,
        productType: productType,
        productId,
      }),
      params: requestData,
    })
      .then(() => {
        dispatch(
          notification.success({
            message: formatText(
              value
                ? 'aggregatorMessages-toggleProductSuccessOn'
                : 'aggregatorMessages-toggleProductSuccessOff'
            ),
          })
        );
      })
      .catch(error => {
        dispatch(toggleActiveEntityFailure(error, productId, !value));
        dispatch(
          notification.error({
            message: formatText(
              value
                ? 'aggregatorMessages-toggleProductErrorOn'
                : 'aggregatorMessages-toggleProductErrorOff'
            ),
          })
        );
      });
  } else {
    const requestData = {
      query: [
        {
          productId,
          productType,
        },
      ],
    };

    return APIPost(dispatch, apiURLBuilder.getURL('aggregatorToggleV1'), requestData)
      .then(() => {
        dispatch(
          notification.success({
            message: formatText(
              value
                ? 'aggregatorMessages-toggleProductSuccessOn'
                : 'aggregatorMessages-toggleProductSuccessOff'
            ),
          })
        );
      })
      .catch(error => {
        dispatch(toggleActiveEntityFailure(error, productId, !value));
        dispatch(
          notification.error({
            message: formatText(
              value
                ? 'aggregatorMessages-toggleProductErrorOn'
                : 'aggregatorMessages-toggleProductErrorOff'
            ),
          })
        );
      });
  }
};

const getGlobalPositionTink =
  (tries = 0) =>
  (dispatch, getState) => {
    const endPointAggregator = 'aggregatorGlobalPositionTink';

    const isFetching = getState().aggregatorData.get('isFetching');
    const error = getState().aggregatorData.get('error');
    let timeoutObject = null;

    const IS_AGGREGATOR_PSD2 = getState().flagBasedFeatures
      ? getState().flagBasedFeatures.get(AGGREGATOR_PSD2)
      : false;

    if (!isFetching && !error) {
      dispatch(getEntitiesDataRequest(false));

      return APIGet({
        dispatch,
        endpoint: endPointAggregator,
        url: apiURLBuilder.getURL(endPointAggregator),
      })
        .then(response => {
          const banks = (response && response.resource.banks) || [];
          const mustStartRequestPoolTink = verifyIfShouldStartRequestPoolTink(banks);

          if (tries <= MAX_GLOBAL_POSITION_REQUESTS) {
            if (mustStartRequestPoolTink) {
              clearTimeout(timeoutObject);
              timeoutObject = setTimeout(
                () => !__CONTACT_CENTER__ && dispatch(getGlobalPositionTink(tries + 1)),
                20000
              );
            }
            try {
              dispatch(
                getEntitiesDataSuccess(
                  parseTinkEntitiesData(
                    response && response.resource,
                    IS_AGGREGATOR_PSD2,
                    mustStartRequestPoolTink
                  ),
                  getState
                )
              );
            } catch (error) {
              clearTimeout(timeoutObject);
              dispatch(getEntitiesDataFailure({ error }));
            }
          } else if (banks.every(verifyHavePendingOrSyncStatusTink)) {
            clearTimeout(timeoutObject);
            dispatch(getEntitiesDataFailure({ error: 'too much tries' }));
          } else {
            clearTimeout(timeoutObject);
            dispatch(getEntitiesDataFailure({ error: null }));
          }
        })
        .catch(error => {
          dispatch(getEntitiesDataFailure(error));
        });
    }
  };

const removeEntityTink = userBankId => dispatch => {
  dispatch(removeEntityRequest());

  const endPointAggregator = 'aggregatorDeleteBankTink';

  APIDel({
    dispatch,
    params: { hideDefaultSnackbar: true },
    endpoint: endPointAggregator,
    url: apiURLBuilder.getURL(endPointAggregator, { userBankId }),
  })
    .then(() => {
      dispatch(removeEntitySuccess(userBankId));
    })
    .then(() => {
      dispatch(
        notification.success({ message: formatText('aggregatorMessages-removeEntitySuccess') })
      );
    })
    .catch(error => {
      dispatch(removeEntityFailure({ error }));
      dispatch(notification.error({ message: formatText('aggregatorMessages-removeEntityError') }));
    });
};

const toggleActiveTink = (productId, productType, value) => dispatch => {
  dispatch(toggleActiveEntityRequest());

  const endpoint = 'aggregatorToggleTink';

  const requestData = {
    query: {
      productsList: [
        {
          productType: productType,
          productId: productId,
          visible: value,
        },
      ],
    },
  };

  APIPatch({
    dispatch,
    endpoint: endpoint,
    url: apiURLBuilder.getURL(endpoint),
    params: requestData,
  })
    .then(() => {
      //dispatch(toggleActiveEntitySuccess(productId, value));
      dispatch(
        notification.success({
          message: formatText(
            value
              ? 'aggregatorMessages-toggleProductSuccessOn'
              : 'aggregatorMessages-toggleProductSuccessOff'
          ),
        })
      );
    })
    .catch(error => {
      dispatch(toggleActiveEntityFailure(error, productId, !value));
      dispatch(
        notification.error({
          message: formatText(
            value
              ? 'aggregatorMessages-toggleProductErrorOn'
              : 'aggregatorMessages-toggleProductErrorOff'
          ),
        })
      );
    });
};

const parseMovementType = type => {
  switch (type) {
    case 'income':
      return 'INCOMES';
    case 'expense':
      return 'EXPENSES';
  }
};

const getTransactionsTink =
  (
    productType,
    productId,
    from,
    to,
    filters = {},
    url = '',
    cursor = '',
    size = MOVEMENTS_PAGE_SIZE
  ) =>
  (dispatch, getState) => {
    //dispatch(getDetailsByProductRequest(productId));

    const requestData = {
      query: {
        cursor,
        limit: size,
        from: filters ? filters.dateFrom : null,
        to: filters ? filters.dateTo : null,
        type: parseMovementType(filters.movementType),
        amountTo: filters ? filters.amountTo : null,
        amountFrom: filters ? filters.amountFrom : null,
      },
    };

    const endPointAggregator = 'aggregatorTransactionsTink';

    return APIGet({
      dispatch,
      endpoint: endPointAggregator,
      url: apiURLBuilder.getURL(endPointAggregator, {
        productType,
        productId,
      }),
      params: requestData,
    })
      .then(response => {
        dispatch(
          getDetailsByProductSuccess(
            productId,
            {
              movements: response ? response.elements : [],
              hasMore: response ? response.metadata.nextCursor : false,
              url,
            },
            cursor ? false : true
          ),
          getState
        );
      })
      .catch(error => {
        dispatch(getDetailsByProductFailure(productId, error));
      });
  };

const getConsentUpdateTinkLinkUrl = (userBankId, credentialId) => dispatch => {
  const endpoint = 'aggregatorGetConsentUpdateTinkLinkUrl';

  dispatch(getConsentUpdateTinkLinkRequest());

  const requestBody = {
    query: {
      callback: `${window.location.origin}/myprofile/global/entities/add`,
      userBankId,
      credentialId,
    },
  };

  return APIPost(dispatch, apiURLBuilder.getURL(endpoint), requestBody)
    .then(response => {
      dispatch(getConsentUpdateTinkLinkSuccess(response));
      window.location.replace(response.tinkLinkUrl);
    })
    .catch(err => {
      getConsentUpdateTinkLinkFailure(err)();
    });
};

export {
  getEntitiesData,
  getProductDetails,
  getSecondFactorAuth,
  removeEntity,
  toggleActive,
  sendSecondAuthKey,
  resetSecondKeyFactorAuth,
  requestNextPage,
  getGlobalPositionTink,
  getTransactionsTink,
  getConsentUpdateTinkLinkUrl,
  removeEntityTink,
  toggleActiveTink,
};
